'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Share2,
  MousePointerClick,
  Search,
  Globe,
  Palette,
  Briefcase,
  TrendingUp,
  BarChart2,
  Users,
  Settings,
} from 'lucide-react';
import CtaLabel, { CTA_ASSESSMENT, CTA_BOOKING } from '@/components/ui/CtaLabel';
import { Arc } from '@/components/instrument';

const serviceData: Record<
  string,
  {
    id: string;
    icon: React.ElementType;
    number: string;
    title: string;
    tagline: string;
    overview: string;
    forWho: string[];
    problems: string[];
    deliverables: string[];
    process: { step: string; title: string; desc: string }[];
    timeline: string;
    faqs: { q: string; a: string }[];
  }
> = {
  'social-media-digital-marketing': {
    id: 'svc-social',
    icon: Share2,
    number: '01',
    title: 'Social Media & Digital Marketing',
    tagline: 'Build an audience that buys',
    overview:
      'We craft engaging social media content and strategies tailored to your audience. From content calendars to community management, we handle everything that turns your social presence into a revenue channel. Our approach is data-driven — we test, measure, and iterate so your content consistently improves.',
    forWho: [
      'SMBs wanting to grow brand awareness',
      'E-commerce brands needing consistent content',
      'Service businesses building trust online',
      'Founders who want to establish thought leadership',
    ],
    problems: [
      'Posting without a clear strategy',
      'Low engagement despite regular content',
      'No system for turning followers into customers',
      'Inconsistent brand voice across platforms',
    ],
    deliverables: [
      'Content calendar (30 days)',
      'Platform strategy (Instagram, LinkedIn, TikTok)',
      'Monthly performance reports',
      'Audience growth plan',
      'Digital product creation (guides, e-books)',
      'Community management guidelines',
    ],
    process: [
      {
        step: '01',
        title: 'Audit & Discovery',
        desc: "We review your existing channels, competitors, and audience data to understand what's working and what isn't.",
      },
      {
        step: '02',
        title: 'Strategy Development',
        desc: 'We build a platform-specific content strategy aligned with your business goals and target audience.',
      },
      {
        step: '03',
        title: 'Content Creation',
        desc: 'Our team produces on-brand content — copy, graphics, and video briefs — ready for your approval.',
      },
      {
        step: '04',
        title: 'Publish & Manage',
        desc: 'We schedule, post, and monitor your content, responding to comments and tracking performance.',
      },
      {
        step: '05',
        title: 'Report & Optimise',
        desc: 'Monthly reports with clear metrics and recommendations for the next cycle.',
      },
    ],
    timeline: '30-day onboarding, then ongoing monthly retainer',
    faqs: [
      {
        q: 'Which platforms do you manage?',
        a: 'We work across Instagram, LinkedIn, Facebook, TikTok, and X (Twitter). We recommend focusing on 2-3 platforms where your audience is most active.',
      },
      {
        q: 'Do you create the content or do we?',
        a: 'We handle content creation end-to-end — strategy, copy, and design briefs. You review and approve before anything goes live.',
      },
      {
        q: 'How long before we see results?',
        a: 'Most clients see meaningful engagement improvements within 60-90 days. Follower growth and lead generation typically accelerate from month 3 onwards.',
      },
      {
        q: 'Can we pause or cancel?',
        a: 'Yes. We work on monthly retainers with 30 days notice required to pause or cancel.',
      },
    ],
  },
  'paid-ads-management': {
    id: 'svc-ads',
    icon: MousePointerClick,
    number: '02',
    title: 'Paid Ads Management',
    tagline: 'Get in front of ready-to-buy customers',
    overview:
      'Expertly managed paid advertising campaigns designed to drive targeted traffic and qualified leads. We handle audience targeting, creative testing, bid optimization, and provide detailed monthly analytics. Every dollar of your ad spend is tracked and optimized for maximum return.',
    forWho: [
      'Businesses ready to scale customer acquisition',
      'E-commerce stores wanting more sales',
      'Service businesses needing qualified leads',
      'Companies with a proven offer ready to amplify',
    ],
    problems: [
      "Wasting budget on ads that don't convert",
      'Reaching the wrong audience',
      'No clear view of ad performance',
      'Campaigns that worked once but plateaued',
    ],
    deliverables: [
      'Facebook & Instagram Ads setup',
      'Google Search & Display Ads',
      'Audience targeting strategy',
      'A/B creative testing',
      'Monthly analytics reports',
      'Conversion tracking setup',
    ],
    process: [
      {
        step: '01',
        title: 'Account Audit',
        desc: 'We review your existing ad accounts, pixel setup, and historical performance to identify quick wins.',
      },
      {
        step: '02',
        title: 'Campaign Architecture',
        desc: 'We design a campaign structure with clear objectives, audience segments, and budget allocation.',
      },
      {
        step: '03',
        title: 'Creative Development',
        desc: 'We brief and produce ad creatives — copy, images, and video concepts — optimized for each platform.',
      },
      {
        step: '04',
        title: 'Launch & Monitor',
        desc: 'Campaigns go live with daily monitoring for the first two weeks to catch any issues early.',
      },
      {
        step: '05',
        title: 'Optimise & Scale',
        desc: 'We continuously test, cut underperformers, and scale what works. Monthly reports keep you informed.',
      },
    ],
    timeline: '2-week setup, then ongoing monthly management',
    faqs: [
      {
        q: 'What ad platforms do you manage?',
        a: 'We manage Meta Ads (Facebook & Instagram), Google Ads (Search, Display, Shopping), and LinkedIn Ads for B2B clients.',
      },
      {
        q: 'What budget do I need to get started?',
        a: 'We recommend a minimum ad spend of $1,500/month to generate meaningful data. Our management fee is separate from your ad spend.',
      },
      {
        q: 'How do you measure success?',
        a: 'We track cost per lead, cost per acquisition, ROAS (return on ad spend), and conversion rate — all reported monthly.',
      },
      {
        q: 'Do you guarantee results?',
        a: "We don't guarantee specific results — no honest agency can. We do guarantee transparent reporting, continuous optimization, and clear communication.",
      },
    ],
  },
  'seo-content-creation': {
    id: 'svc-seo',
    icon: Search,
    number: '03',
    title: 'SEO & Content Creation',
    tagline: 'Grow traffic that compounds over time',
    overview:
      'Comprehensive SEO strategies combined with professionally written, optimized content. We fix technical issues, create helpful content, and earn authoritative links to keep your site ranking. Unlike paid ads, SEO builds an asset that compounds — the longer you invest, the more it returns.',
    forWho: [
      'Businesses wanting sustainable organic growth',
      'Companies tired of relying solely on paid ads',
      'Brands wanting to establish authority in their niche',
      'Businesses with a long sales cycle needing content to educate buyers',
    ],
    problems: [
      'Invisible on Google while competitors rank',
      "Traffic that doesn't convert",
      'No content strategy or publishing cadence',
      'Technical SEO issues holding back rankings',
    ],
    deliverables: [
      'Technical SEO audit & fixes',
      'Keyword research & strategy',
      'Monthly blog articles (2-4)',
      'On-page optimization',
      'Link building outreach',
      'Monthly ranking reports',
    ],
    process: [
      {
        step: '01',
        title: 'Technical Audit',
        desc: 'We crawl your site to identify and fix technical issues — speed, indexing, structured data, and mobile usability.',
      },
      {
        step: '02',
        title: 'Keyword Strategy',
        desc: 'We research keywords your ideal customers use and map them to your pages and content plan.',
      },
      {
        step: '03',
        title: 'On-Page Optimisation',
        desc: 'We optimise existing pages — titles, meta descriptions, headings, and internal links.',
      },
      {
        step: '04',
        title: 'Content Creation',
        desc: 'We write and publish SEO-optimized articles and landing pages that rank and convert.',
      },
      {
        step: '05',
        title: 'Link Building',
        desc: 'We earn high-quality backlinks through outreach, guest posts, and digital PR.',
      },
    ],
    timeline: '3-month minimum recommended; results typically visible from month 4-6',
    faqs: [
      {
        q: 'How long does SEO take to work?',
        a: 'SEO is a long-term investment. Most clients see meaningful ranking improvements within 4-6 months, with significant traffic growth by month 9-12.',
      },
      {
        q: 'Do you write the content or do we?',
        a: 'We handle content creation end-to-end. You provide subject matter expertise; we handle research, writing, and optimization.',
      },
      {
        q: 'What if my site has technical issues?',
        a: "We identify and fix technical SEO issues as part of onboarding. For major development work, we'll coordinate with your developer.",
      },
      {
        q: 'How do you report on progress?',
        a: 'Monthly reports covering keyword rankings, organic traffic, backlinks earned, and content published.',
      },
    ],
  },
  'website-creation': {
    id: 'svc-website',
    icon: Globe,
    number: '04',
    title: 'Website Creation',
    tagline: 'A site that works as hard as you do',
    overview:
      'We build conversion-optimized websites and online stores with drag-and-drop editing, secure payments, and built-in analytics. Fast, accessible, and designed to grow with your business. Every site we build is mobile-first, SEO-ready, and built to convert visitors into customers.',
    forWho: [
      'Businesses with an outdated or ineffective website',
      'Startups needing a professional online presence',
      'E-commerce brands wanting a better store',
      'Service businesses needing a lead-generating site',
    ],
    problems: [
      'Outdated website losing customers',
      "Site that's hard to update without a developer",
      'Poor mobile experience',
      'Slow load times hurting conversions and SEO',
    ],
    deliverables: [
      'Custom website design',
      'Mobile-first development',
      'E-commerce integration',
      'CMS setup (self-editable)',
      'Speed & performance optimization',
      'Analytics & tracking setup',
    ],
    process: [
      {
        step: '01',
        title: 'Discovery & Wireframes',
        desc: 'We map out your site structure, user journeys, and key conversion points before any design begins.',
      },
      {
        step: '02',
        title: 'Design',
        desc: 'We create high-fidelity designs aligned with your brand. You review and approve before development starts.',
      },
      {
        step: '03',
        title: 'Development',
        desc: 'We build your site on a platform you can manage — typically WordPress, Webflow, or Shopify.',
      },
      {
        step: '04',
        title: 'Content & SEO',
        desc: 'We populate your site with optimized content and configure on-page SEO from day one.',
      },
      {
        step: '05',
        title: 'Launch & Handover',
        desc: 'We launch, test across devices, and train your team to manage the site independently.',
      },
    ],
    timeline: '4-8 weeks depending on scope',
    faqs: [
      {
        q: 'What platform do you build on?',
        a: 'We typically build on WordPress, Webflow, or Shopify depending on your needs. We recommend the platform that gives you the most independence.',
      },
      {
        q: 'Will I be able to update it myself?',
        a: 'Yes. Every site we build includes a CMS that lets you update content, add pages, and manage products without coding.',
      },
      {
        q: 'Do you offer hosting?',
        a: "We can recommend and set up hosting, but we don't lock you into our hosting. You own your site and can move it if needed.",
      },
      {
        q: 'What about ongoing maintenance?',
        a: 'We offer optional monthly maintenance packages covering updates, backups, and minor content changes.',
      },
    ],
  },
  'digital-design': {
    id: 'svc-design',
    icon: Palette,
    number: '05',
    title: 'Digital Design',
    tagline: 'Visuals that tell your story clearly',
    overview:
      "Stand-out visuals for every platform. We craft logos, banners, social posts, and UI mock-ups that communicate your brand clearly. You receive organized, editable templates your team can update in minutes. Good design isn't decoration — it's how your business communicates trust and professionalism before a word is read.",
    forWho: [
      'Businesses with inconsistent or outdated branding',
      'Startups building their visual identity from scratch',
      'Marketing teams needing a reliable design partner',
      'Businesses launching new products or campaigns',
    ],
    problems: [
      'Inconsistent branding across channels',
      'DIY design that looks unprofessional',
      'No brand guidelines for the team to follow',
      'Slow turnaround on design requests',
    ],
    deliverables: [
      'Logo & brand identity',
      'Social media templates',
      'Banners & ad creatives',
      'UI mock-ups',
      'Brand style guide',
      'Presentation templates',
    ],
    process: [
      {
        step: '01',
        title: 'Brand Discovery',
        desc: 'We run a discovery session to understand your values, audience, competitors, and visual preferences.',
      },
      {
        step: '02',
        title: 'Concept Development',
        desc: 'We present 2-3 distinct design directions for your feedback before committing to a final direction.',
      },
      {
        step: '03',
        title: 'Design Refinement',
        desc: "We refine the chosen direction through 2 rounds of revisions until you're fully satisfied.",
      },
      {
        step: '04',
        title: 'Asset Production',
        desc: 'We produce all agreed deliverables in the correct formats for print, web, and social media.',
      },
      {
        step: '05',
        title: 'Handover & Guidelines',
        desc: 'You receive all files plus a brand style guide so your team can maintain consistency going forward.',
      },
    ],
    timeline: '2-4 weeks for brand identity; ongoing retainer available for design support',
    faqs: [
      {
        q: 'Do you do logo design only?',
        a: 'We can do logo-only projects, but we recommend a full brand identity package to ensure consistency across all touchpoints.',
      },
      {
        q: 'What file formats do we receive?',
        a: 'You receive all source files (AI, PSD, Figma) plus export-ready files in PNG, SVG, PDF, and JPG formats.',
      },
      {
        q: 'How many revisions are included?',
        a: 'All projects include 2 rounds of revisions. Additional revisions are available at an hourly rate.',
      },
      {
        q: 'Can you match our existing brand?',
        a: 'Yes. If you have existing brand elements you want to keep, we work within those constraints while improving consistency.',
      },
    ],
  },
  'business-consulting': {
    id: 'svc-consulting',
    icon: Briefcase,
    number: '06',
    title: 'Business Consulting',
    tagline: 'Clarity on what to do next',
    overview:
      'Our advisors analyze your data, processes, and market to uncover hidden growth opportunities. From pricing and product-market fit to operational efficiency, we deliver a strategic roadmap with prioritized next steps. We work with founders and business leaders who are serious about growth but need an outside perspective to cut through the noise.',
    forWho: [
      'Founders feeling stuck or overwhelmed',
      'SMBs preparing to scale',
      'Businesses entering new markets',
      'Companies needing a strategic review before major investment',
    ],
    problems: [
      'No clear growth strategy',
      'Unclear customer segments',
      'Operational bottlenecks limiting growth',
      'Unsure which marketing channels to prioritize',
    ],
    deliverables: [
      'Business diagnostic session',
      'Growth opportunity analysis',
      'Strategic roadmap (90-day)',
      'Mentorship & accountability',
      'Startup program access',
      'Competitive landscape review',
    ],
    process: [
      {
        step: '01',
        title: 'Business Diagnostic',
        desc: 'A structured 2-hour session to understand your business model, current challenges, and growth goals.',
      },
      {
        step: '02',
        title: 'Data & Market Analysis',
        desc: 'We review your financials, customer data, and competitive landscape to identify patterns and opportunities.',
      },
      {
        step: '03',
        title: 'Strategy Development',
        desc: 'We develop a clear strategic roadmap with prioritized initiatives and measurable milestones.',
      },
      {
        step: '04',
        title: 'Roadmap Presentation',
        desc: 'We present findings and recommendations in a clear, actionable format — no jargon, just next steps.',
      },
      {
        step: '05',
        title: 'Implementation Support',
        desc: 'Optional ongoing advisory support to help you execute the strategy and stay accountable.',
      },
    ],
    timeline: 'Initial engagement: 2-4 weeks. Ongoing advisory: monthly retainer',
    faqs: [
      {
        q: 'Is this just strategy or do you help with execution too?',
        a: 'Both. We start with strategy and can stay on as an advisory partner to support execution, or hand off a clear roadmap for your team to implement.',
      },
      {
        q: 'What industries do you work with?',
        a: 'We work primarily with hospitality, e-commerce, and professional services businesses in Europe and Australia.',
      },
      {
        q: 'What do you need from us to get started?',
        a: 'A willingness to share honest data about your business — revenue, costs, customer metrics, and current challenges. The more transparent you are, the better our recommendations.',
      },
      {
        q: 'How is this different from hiring a business coach?',
        a: "We combine strategic analysis with marketing execution expertise. We don't just advise — we can implement alongside you through our other service lines.",
      },
    ],
  },
  'business-strategy': {
    id: 'svc-strategy',
    icon: TrendingUp,
    number: '07',
    title: 'Business Strategy',
    tagline: 'Define clear direction and measurable growth objectives',
    overview:
      'We help you build a resilient, scalable business framework with clear competitive positioning and actionable growth objectives. Our strategic process combines market analysis, competitive intelligence, and financial modeling to give you a roadmap that actually works in the real world.',
    forWho: [
      'Founders defining their long-term vision',
      'SMBs ready to move beyond reactive decision-making',
      'Businesses entering new markets or verticals',
      'Companies preparing for investment or acquisition',
    ],
    problems: [
      'No clear long-term direction',
      'Reactive decision-making without a framework',
      'Unclear competitive positioning',
      'Growth stalling without a strategic plan',
    ],
    deliverables: [
      'Competitive positioning analysis',
      'Strategic growth roadmap (12-month)',
      'Market opportunity assessment',
      'KPI framework & tracking system',
      'Quarterly review cadence',
      'Executive strategy presentation',
    ],
    process: [
      {
        step: '01',
        title: 'Vision & Goals Workshop',
        desc: 'We facilitate a structured session to align leadership on vision, mission, and 12-month growth targets.',
      },
      {
        step: '02',
        title: 'Market & Competitive Analysis',
        desc: 'We map your competitive landscape, identify white space, and benchmark your positioning.',
      },
      {
        step: '03',
        title: 'Strategy Formulation',
        desc: 'We develop strategic options and recommend the highest-leverage path forward.',
      },
      {
        step: '04',
        title: 'Roadmap Development',
        desc: 'We translate strategy into a prioritized 12-month roadmap with clear milestones and owners.',
      },
      {
        step: '05',
        title: 'Alignment & Rollout',
        desc: 'We present the strategy to your team and support the communication and rollout process.',
      },
    ],
    timeline: '3-4 week engagement; quarterly strategy reviews available',
    faqs: [
      {
        q: 'How is this different from business consulting?',
        a: 'Business Strategy focuses specifically on long-term direction and competitive positioning. Business Consulting is broader and includes operational and marketing elements.',
      },
      {
        q: 'Do you work with early-stage startups?',
        a: 'Yes. We tailor our approach to your stage — early-stage work focuses on product-market fit and go-to-market; growth-stage work focuses on scaling and competitive moats.',
      },
      {
        q: 'How long does the strategy engagement take?',
        a: 'Typically 3-4 weeks for the initial strategy development, with optional quarterly reviews to adapt as the market evolves.',
      },
      {
        q: 'Will you help us implement the strategy?',
        a: 'Yes. We can stay on as an advisory partner or connect you with our execution teams across marketing, operations, and digital.',
      },
    ],
  },
  'brand-positioning': {
    id: 'svc-brand',
    icon: Palette,
    number: '08',
    title: 'Brand Positioning',
    tagline: 'Establish authority and recognition in your sector',
    overview:
      'We craft a distinct market identity that resonates with your ideal customers and sets you apart from competitors. From brand messaging and visual identity to positioning strategy, we give your business the clarity and consistency it needs to build lasting recognition.',
    forWho: [
      'Businesses with inconsistent or unclear brand messaging',
      'Companies launching new products or entering new markets',
      'Startups building their identity from scratch',
      'Established businesses ready for a brand refresh',
    ],
    problems: [
      'Unclear or inconsistent brand message',
      'Struggling to differentiate from competitors',
      "Target audience doesn't understand your value",
      "Brand visuals don't reflect your quality",
    ],
    deliverables: [
      'Brand positioning statement',
      'Messaging framework & tone of voice',
      'Visual identity guidelines',
      'Competitor differentiation map',
      'Brand story & narrative',
      'Implementation playbook',
    ],
    process: [
      {
        step: '01',
        title: 'Brand Audit',
        desc: 'We review your current brand assets, messaging, and market perception to identify gaps and opportunities.',
      },
      {
        step: '02',
        title: 'Audience & Competitor Research',
        desc: 'We map your ideal customer profiles and analyze how competitors position themselves.',
      },
      {
        step: '03',
        title: 'Positioning Strategy',
        desc: "We define your unique positioning — the space you own in your customers' minds.",
      },
      {
        step: '04',
        title: 'Messaging Development',
        desc: 'We craft your brand story, key messages, and tone of voice guidelines.',
      },
      {
        step: '05',
        title: 'Visual Identity & Rollout',
        desc: 'We develop or refine your visual identity and create an implementation plan.',
      },
    ],
    timeline: '3-5 weeks for full brand positioning; visual identity adds 2-3 weeks',
    faqs: [
      {
        q: 'Do you do logo design as part of this?',
        a: 'Visual identity work (including logo refinement) is included. Full logo design from scratch is available as an add-on.',
      },
      {
        q: 'How do you measure brand positioning success?',
        a: 'We track brand recall, message clarity scores, and competitive differentiation through customer surveys and market feedback.',
      },
      {
        q: 'Can you work with our existing brand guidelines?',
        a: "Yes. We can work within your existing guidelines or recommend updates where they're limiting your positioning.",
      },
      {
        q: 'What if we already have a marketing team?',
        a: 'We work alongside your team, providing the strategic positioning framework they need to execute consistently.',
      },
    ],
  },
  'market-research': {
    id: 'svc-research',
    icon: Search,
    number: '09',
    title: 'Market Research',
    tagline: 'Make decisions backed by verified market intelligence',
    overview:
      'We uncover demand signals, competitor gaps, and untapped customer segments through rigorous primary and secondary research. Our market intelligence reports give you the data you need to make confident decisions about product development, market entry, and growth strategy.',
    forWho: [
      'Businesses launching new products or services',
      'Companies entering new geographic markets',
      'Founders validating business model assumptions',
      'Established businesses facing unexpected competitive pressure',
    ],
    problems: [
      'Making decisions based on assumptions, not data',
      'Unclear who your best customers actually are',
      "Missing competitor moves until it's too late",
      'Wasting budget on markets with low demand',
    ],
    deliverables: [
      'Customer segmentation analysis',
      'Competitor intelligence report',
      'Demand & opportunity sizing',
      'Customer interview synthesis',
      'Market entry recommendations',
      'Ongoing monitoring dashboard',
    ],
    process: [
      {
        step: '01',
        title: 'Research Design',
        desc: 'We define research objectives, methodology, and success criteria aligned with your business decisions.',
      },
      {
        step: '02',
        title: 'Primary Research',
        desc: 'We conduct customer interviews, surveys, and focus groups to gather first-hand market intelligence.',
      },
      {
        step: '03',
        title: 'Secondary Research',
        desc: 'We analyze industry reports, competitor data, and market trends to build a complete picture.',
      },
      {
        step: '04',
        title: 'Analysis & Synthesis',
        desc: 'We identify patterns, validate hypotheses, and extract actionable insights from the data.',
      },
      {
        step: '05',
        title: 'Recommendations & Reporting',
        desc: 'We deliver a clear report with prioritized recommendations and decision-ready insights.',
      },
    ],
    timeline: '2-4 weeks depending on research scope and methodology',
    faqs: [
      {
        q: 'Do you conduct the customer interviews yourselves?',
        a: 'Yes. We handle recruitment, facilitation, and synthesis of all primary research. You receive the insights, not raw transcripts.',
      },
      {
        q: 'How many customers do you interview?',
        a: 'Typically 8-15 in-depth interviews for qualitative research, supplemented by quantitative surveys of 100+ respondents where needed.',
      },
      {
        q: 'Can you research international markets?',
        a: 'Yes. We have experience researching markets across Europe, Australia, and Southeast Asia.',
      },
      {
        q: 'What if the research contradicts our assumptions?',
        a: "That's often the most valuable outcome. We present findings objectively and help you adapt your strategy accordingly.",
      },
    ],
  },
  'growth-marketing': {
    id: 'svc-growth',
    icon: BarChart2,
    number: '10',
    title: 'Growth Marketing',
    tagline: 'Build a repeatable customer acquisition system',
    overview:
      'We build a repeatable customer acquisition system across every channel — from paid ads and SEO to referral programs and partnerships. Our growth marketing approach is data-driven and experiment-led, focused on finding the highest-leverage channels for your specific business.',
    forWho: [
      'Businesses with a proven offer ready to scale',
      'Companies with inconsistent or unpredictable lead flow',
      'Startups moving from founder-led sales to scalable acquisition',
      'Businesses that have plateaued on their current channels',
    ],
    problems: [
      'Inconsistent or unpredictable customer acquisition',
      'Over-reliance on a single channel',
      'High customer acquisition cost with low ROI',
      'No systematic approach to testing new channels',
    ],
    deliverables: [
      'Growth audit & opportunity map',
      'Multi-channel acquisition strategy',
      'Experiment roadmap (90-day)',
      'Channel-specific playbooks',
      'Growth metrics dashboard',
      'Monthly performance reviews',
    ],
    process: [
      {
        step: '01',
        title: 'Growth Audit',
        desc: 'We audit your current acquisition channels, conversion rates, and unit economics to find the biggest levers.',
      },
      {
        step: '02',
        title: 'Channel Strategy',
        desc: 'We identify the highest-potential channels for your business and build a prioritized testing roadmap.',
      },
      {
        step: '03',
        title: 'Experiment Design',
        desc: 'We design rapid experiments to validate channel assumptions before committing significant budget.',
      },
      {
        step: '04',
        title: 'Execute & Measure',
        desc: 'We run experiments, track results rigorously, and double down on what works.',
      },
      {
        step: '05',
        title: 'Scale & Systematize',
        desc: 'We build repeatable systems around winning channels and document playbooks for your team.',
      },
    ],
    timeline: '90-day sprint to establish core channels; ongoing retainer to scale',
    faqs: [
      {
        q: 'How is growth marketing different from digital marketing?',
        a: 'Growth marketing is broader — it covers the full funnel from acquisition to retention, and includes product, referral, and partnership channels alongside paid and organic.',
      },
      {
        q: 'What budget do I need?',
        a: 'We recommend a minimum experiment budget of $2,000/month across channels. Our management fee is separate.',
      },
      {
        q: 'How quickly will we see results?',
        a: 'Initial data from experiments typically comes within 30-45 days. Meaningful channel validation takes 60-90 days.',
      },
      {
        q: 'Do you handle execution or just strategy?',
        a: 'Both. We handle strategy, experiment design, and execution — including ad management, content, and analytics setup.',
      },
    ],
  },
  'lead-generation': {
    id: 'svc-leads',
    icon: Users,
    number: '11',
    title: 'Lead Generation',
    tagline: 'Fill your pipeline with sales-ready opportunities',
    overview:
      'We design high-converting funnels that attract and qualify your best prospects. From lead magnets and landing pages to email sequences and CRM setup, we build the infrastructure that keeps your pipeline full of sales-ready leads — consistently.',
    forWho: [
      'B2B businesses needing a consistent flow of qualified leads',
      'Service businesses relying on referrals and wanting to diversify',
      'Sales teams with capacity but not enough pipeline',
      'Companies launching new services or entering new markets',
    ],
    problems: [
      'Inconsistent or unpredictable lead flow',
      "Leads that aren't qualified or ready to buy",
      'No system to nurture prospects over time',
      'Sales team spending time on unqualified conversations',
    ],
    deliverables: [
      'Lead magnet creation',
      'Landing page design & copy',
      'Email nurture sequences',
      'CRM setup & automation',
      'Lead scoring framework',
      'Monthly pipeline reports',
    ],
    process: [
      {
        step: '01',
        title: 'Ideal Customer Profiling',
        desc: 'We define your ideal customer profile and the triggers that indicate buying intent.',
      },
      {
        step: '02',
        title: 'Funnel Architecture',
        desc: 'We design a lead generation funnel matched to your sales cycle and buyer journey.',
      },
      {
        step: '03',
        title: 'Asset Creation',
        desc: 'We create lead magnets, landing pages, and email sequences that convert and qualify.',
      },
      {
        step: '04',
        title: 'Traffic & Distribution',
        desc: 'We drive targeted traffic to your funnel through paid ads, SEO, and outbound channels.',
      },
      {
        step: '05',
        title: 'Optimise & Report',
        desc: 'We track conversion rates at every stage and continuously improve funnel performance.',
      },
    ],
    timeline: '3-4 weeks to build; ongoing optimization on monthly retainer',
    faqs: [
      {
        q: 'What counts as a qualified lead?',
        a: 'We work with you to define qualification criteria — typically based on company size, budget, timeline, and decision-making authority.',
      },
      {
        q: 'Do you integrate with our CRM?',
        a: 'Yes. We integrate with HubSpot, Salesforce, Pipedrive, and most major CRM platforms.',
      },
      {
        q: 'How many leads can we expect?',
        a: 'Volume depends on your market size and budget. We focus on lead quality over quantity — fewer, better-qualified leads close faster.',
      },
      {
        q: 'What if our sales cycle is very long?',
        a: 'Long sales cycles are where nurture sequences shine. We build email and content sequences that keep you top-of-mind over months.',
      },
    ],
  },
  'customer-experience': {
    id: 'svc-cx',
    icon: Users,
    number: '12',
    title: 'Customer Experience',
    tagline: 'Turn customers into loyal advocates',
    overview:
      'We map and optimize every touchpoint in your customer journey to reduce churn, increase lifetime value, and turn satisfied customers into active advocates. Our CX work combines journey mapping, feedback systems, and operational improvements to create experiences that customers remember and recommend.',
    forWho: [
      'Businesses with high churn or low repeat purchase rates',
      'Companies receiving inconsistent customer feedback',
      'Service businesses wanting to systematize their delivery',
      'Brands looking to build a referral and advocacy program',
    ],
    problems: [
      'High customer churn despite good acquisition',
      'Inconsistent service delivery across touchpoints',
      'No system for collecting and acting on customer feedback',
      'Customers not referring or advocating for the brand',
    ],
    deliverables: [
      'Customer journey map',
      'Touchpoint audit & gap analysis',
      'NPS & feedback system setup',
      'Churn reduction playbook',
      'Loyalty & advocacy program design',
      'CX metrics dashboard',
    ],
    process: [
      {
        step: '01',
        title: 'Journey Mapping',
        desc: 'We map every touchpoint from first awareness to post-purchase advocacy, identifying friction and delight moments.',
      },
      {
        step: '02',
        title: 'Customer Research',
        desc: 'We interview customers and analyze feedback data to understand their actual experience vs. your intended experience.',
      },
      {
        step: '03',
        title: 'Gap Analysis',
        desc: 'We identify the highest-impact gaps between current and ideal customer experience.',
      },
      {
        step: '04',
        title: 'Improvement Design',
        desc: 'We design specific improvements for each gap — from process changes to communication templates.',
      },
      {
        step: '05',
        title: 'Implementation & Measurement',
        desc: 'We support implementation and set up measurement systems to track CX improvement over time.',
      },
    ],
    timeline: '4-6 weeks for full CX audit and roadmap; implementation support ongoing',
    faqs: [
      {
        q: 'How do you measure customer experience improvement?',
        a: 'We use NPS, CSAT, customer effort score, churn rate, and repeat purchase rate as primary metrics.',
      },
      {
        q: 'Do you work with B2B or B2C businesses?',
        a: 'Both. B2B CX focuses on account management and onboarding; B2C focuses on purchase journey and post-sale engagement.',
      },
      {
        q: "What if we don't have much customer feedback data?",
        a: 'We start by building feedback collection systems, then use qualitative interviews to gather initial insights.',
      },
      {
        q: 'Can you help us build a referral program?',
        a: 'Yes. Referral and advocacy program design is a core part of our CX work.',
      },
    ],
  },
  'business-operations': {
    id: 'svc-ops',
    icon: Settings,
    number: '13',
    title: 'Business Operations',
    tagline: 'Operate efficiently at every stage of growth',
    overview:
      'We streamline your processes, remove bottlenecks, and build scalable operational systems that let your business grow without breaking. From workflow automation and team structure to supplier management and performance tracking, we build the operational backbone your business needs.',
    forWho: [
      'Businesses growing faster than their systems can handle',
      'Founders spending too much time in day-to-day operations',
      'Companies with inconsistent delivery or quality issues',
      'Businesses preparing to hire or scale their team',
    ],
    problems: [
      'Processes that break as the business grows',
      'Too much founder involvement in day-to-day tasks',
      'Inconsistent delivery quality across the team',
      'No visibility into operational performance',
    ],
    deliverables: [
      'Process audit & documentation',
      'Workflow automation setup',
      'Team structure & role design',
      'Performance tracking system',
      'Standard operating procedures (SOPs)',
      'Operations playbook',
    ],
    process: [
      {
        step: '01',
        title: 'Operations Audit',
        desc: 'We map your current processes, identify bottlenecks, and quantify the cost of operational inefficiency.',
      },
      {
        step: '02',
        title: 'Process Design',
        desc: 'We redesign core processes for efficiency, consistency, and scalability.',
      },
      {
        step: '03',
        title: 'Automation & Tooling',
        desc: 'We identify and implement automation opportunities using tools your team will actually use.',
      },
      {
        step: '04',
        title: 'Documentation & SOPs',
        desc: 'We document all processes in clear, actionable SOPs that new team members can follow from day one.',
      },
      {
        step: '05',
        title: 'Training & Handover',
        desc: 'We train your team on new processes and set up monitoring systems to ensure adoption.',
      },
    ],
    timeline: '4-8 weeks depending on business complexity; ongoing advisory available',
    faqs: [
      {
        q: 'What tools do you use for automation?',
        a: 'We work with tools your team already uses where possible — Zapier, Make, HubSpot, Notion, Airtable, and others depending on your stack.',
      },
      {
        q: 'Do you help with hiring and team structure?',
        a: 'Yes. We can advise on role design, hiring criteria, and org structure as part of the operations engagement.',
      },
      {
        q: 'How disruptive is the process improvement work?',
        a: 'We design for minimal disruption — changes are phased and tested before full rollout. Your business keeps running throughout.',
      },
      {
        q: 'What if our team resists the new processes?',
        a: 'Change management is built into our approach. We involve your team in the design process to build buy-in from the start.',
      },
    ],
  },
  'digital-marketing': {
    id: 'svc-digital-mktg',
    icon: Globe,
    number: '14',
    title: 'Digital Marketing',
    tagline: 'Maximize reach, engagement, and measurable return',
    overview:
      'We execute integrated campaigns across search, social, and paid channels to maximize your reach, engagement, and measurable return. Our digital marketing approach combines data-driven strategy with creative execution — ensuring every campaign is optimized for your specific business goals and audience.',
    forWho: [
      'Businesses wanting a unified digital marketing strategy',
      'Companies running disconnected campaigns across channels',
      'Brands looking to improve digital ROI',
      'Businesses entering new markets online',
    ],
    problems: [
      'Fragmented campaigns with no unified strategy',
      'Poor ROI from digital spend',
      'Inconsistent brand presence across channels',
      'No clear attribution or performance visibility',
    ],
    deliverables: [
      'Integrated channel strategy',
      'Campaign planning & execution',
      'Search, social & paid media management',
      'Performance analytics dashboard',
      'Monthly reporting & optimization',
      'Audience targeting & segmentation',
    ],
    process: [
      {
        step: '01',
        title: 'Digital Audit',
        desc: 'We audit your current digital presence, channels, and spend to identify gaps and quick wins.',
      },
      {
        step: '02',
        title: 'Strategy Development',
        desc: 'We build an integrated digital strategy aligned with your business goals and target audience.',
      },
      {
        step: '03',
        title: 'Campaign Setup',
        desc: 'We configure campaigns across search, social, and paid channels with proper tracking and attribution.',
      },
      {
        step: '04',
        title: 'Execute & Monitor',
        desc: 'We launch campaigns and monitor performance daily, making real-time optimizations.',
      },
      {
        step: '05',
        title: 'Report & Scale',
        desc: "Monthly reports with clear metrics and recommendations. We scale what works and cut what doesn't.",
      },
    ],
    timeline: '2-week setup, then ongoing monthly management',
    faqs: [
      {
        q: 'Which digital channels do you manage?',
        a: 'We manage search (Google Ads, SEO), social (Meta, LinkedIn, TikTok), email, and display advertising — tailored to where your audience is most active.',
      },
      {
        q: 'How do you measure digital marketing success?',
        a: 'We track reach, engagement, conversion rate, cost per acquisition, and ROAS — all reported in a clear monthly dashboard.',
      },
      {
        q: 'Do you handle creative as well as media buying?',
        a: 'Yes. We handle strategy, creative direction, media buying, and optimization end-to-end.',
      },
      {
        q: 'How is this different from paid ads management?',
        a: 'Digital Marketing is broader — it covers the full digital ecosystem including organic, paid, and owned channels. Paid Ads Management focuses specifically on paid media.',
      },
    ],
  },
};

/** Read off the data, so adding a service cannot leave the dial claiming a
 *  total that is no longer true. */
const SERVICE_COUNT = Object.keys(serviceData).length;

const relatedServices: Record<string, string[]> = {
  'social-media-digital-marketing': ['paid-ads-management', 'digital-design'],
  'paid-ads-management': ['social-media-digital-marketing', 'seo-content-creation'],
  'seo-content-creation': ['website-creation', 'paid-ads-management'],
  'website-creation': ['digital-design', 'seo-content-creation'],
  'digital-design': ['website-creation', 'social-media-digital-marketing'],
  'business-consulting': ['seo-content-creation', 'paid-ads-management'],
  'business-strategy': ['business-consulting', 'market-research'],
  'brand-positioning': ['digital-design', 'business-strategy'],
  'market-research': ['business-strategy', 'growth-marketing'],
  'growth-marketing': ['lead-generation', 'paid-ads-management'],
  'lead-generation': ['growth-marketing', 'customer-experience'],
  'customer-experience': ['business-operations', 'lead-generation'],
  'business-operations': ['business-strategy', 'business-consulting'],
  'digital-marketing': ['paid-ads-management', 'social-media-digital-marketing'],
};

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(var(--m-ink-rgb),0.1)' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span
          className="text-sm font-semibold pr-4"
          style={{
            color: 'var(--m-ink)',
            fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
          }}
        >
          {q}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--m-muted)' }} />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--m-muted)' }} />
        )}
      </button>
      {open && (
        <div
          className="pb-4 text-sm leading-relaxed"
          style={{
            color: 'var(--m-ink-5)',
            fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
          }}
        >
          {a}
        </div>
      )}
    </div>
  );
}

export default function ServiceDetailClient({ slug }: { slug: string }) {
  const service = serviceData[slug];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center m-ground">
        <div className="text-center">
          <p
            className="font-mono text-[10px] uppercase tracking-widest mb-4"
            style={{ color: 'var(--m-muted)' }}
          >
            ERROR — SERVICE NOT FOUND
          </p>
          <h1
            className="text-2xl font-bold mb-6"
            style={{
              color: 'var(--m-ink)',
              fontFamily: "'Archivo Narrow', sans-serif",
              letterSpacing: '0.02em',
            }}
          >
            Service not found
          </h1>
          <Link
            href="/services"
            className="inline-block font-mono uppercase text-[10px] tracking-widest px-6 py-3"
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.3)',
              color: 'var(--m-ink)',
              textDecoration: 'none',
            }}
          >
            [ Back to Services ]
          </Link>
        </div>
      </div>
    );
  }

  const related = relatedServices[slug] || [];
  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen m-ground">
      {/* Hero — dark band */}
      <section
        data-ground
        style={{
          background: 'var(--m-band)',
          borderBottom: '1px solid var(--m-band-edge)',
          paddingTop: 96,
          paddingBottom: 64,
        }}
      >
        <div className="m-container relative">
          {/* The hero left its right half empty. The service's own number,
              plotted on the arc — fourteen services, this one marked — is a
              real reading rather than ornament, which is the only thing that
              earns this motif a second appearance. Hidden below lg, where the
              column is full width and there is no empty half to fill. */}
          <div
            className="hidden lg:block absolute pointer-events-none"
            style={{ right: 40, top: -8 }}
            aria-hidden="true"
          >
            <Arc
              value={Number(service.number) / SERVICE_COUNT}
              size={230}
              sweep={0.6}
              start={0.2}
              ticks={SERVICE_COUNT}
              onDark
            >
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  color: 'rgba(var(--m-on-band-rgb),0.5)',
                }}
              >
                SVC {service.number}
              </span>
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  color: 'rgba(var(--m-on-band-rgb),0.3)',
                  marginTop: 4,
                }}
              >
                of {SERVICE_COUNT}
              </span>
            </Arc>
          </div>

          <Link
            href="/services"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest mb-10 transition-opacity hover:opacity-70"
            style={{ color: 'rgba(var(--m-on-band-rgb),0.5)', textDecoration: 'none' }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Services
          </Link>

          <div className="flex items-start gap-5 mb-6">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 48,
                height: 48,
                border: '1px solid rgba(var(--m-on-band-rgb),0.15)',
                background: 'rgba(var(--m-on-band-rgb),0.05)',
              }}
            >
              <ServiceIcon
                className="w-5 h-5"
                style={{ color: 'rgba(var(--m-on-band-rgb),0.7)' }}
              />
            </div>
            <div>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-2"
                style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
              >
                Service {service.number}
              </p>
              <h1
                className="font-bold uppercase"
                style={{
                  fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                  fontSize: 'clamp(24px, 4vw, 42px)',
                  color: 'var(--m-on-band)',
                  letterSpacing: '0.03em',
                  lineHeight: 1.05,
                }}
              >
                {service.title}
              </h1>
            </div>
          </div>

          <p
            className="font-mono uppercase tracking-widest mb-5"
            style={{
              fontSize: 11,
              color: 'rgba(var(--m-on-band-rgb),0.45)',
              letterSpacing: '0.12em',
            }}
          >
            {service.tagline}
          </p>

          <p
            style={{
              fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
              fontSize: 15,
              color: 'rgba(var(--m-on-band-rgb),0.6)',
              lineHeight: 1.65,
              maxWidth: 600,
              marginBottom: 32,
            }}
          >
            {service.overview}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/book-a-consultation"
              className="inline-flex items-center justify-center gap-2 font-mono uppercase text-[12px] tracking-widest px-8 transition-opacity hover:opacity-85"
              style={{
                background: 'var(--m-on-band)',
                color: 'var(--m-band)',
                textDecoration: 'none',
                minHeight: 52,
                letterSpacing: '0.16em',
              }}
            >
              Book a call
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              href="/business-assessment"
              className="inline-flex items-center justify-center gap-2 font-mono uppercase text-[12px] tracking-widest px-8 transition-opacity hover:opacity-85"
              style={{
                minHeight: 52,
                letterSpacing: '0.16em',
                border: '1px solid rgba(var(--m-on-band-rgb),0.25)',
                color: 'rgba(var(--m-on-band-rgb),0.7)',
                textDecoration: 'none',
              }}
            >
              [ Take the Assessment ]
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <div data-ground className="m-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-14">
            {/* Who it's for */}
            <div>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-5"
                style={{ color: 'var(--m-muted)' }}
              >
                Who This Is For
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.forWho.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 py-3"
                    style={{ borderBottom: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
                  >
                    <div
                      className="w-1 h-1 rounded-full flex-shrink-0 mt-2"
                      style={{ background: 'var(--m-ink)' }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--m-ink-3)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        lineHeight: 1.5,
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Problems solved */}
            <div>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-5"
                style={{ color: 'var(--m-muted)' }}
              >
                Problems We Solve
              </p>
              <ul className="space-y-3">
                {service.problems.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span
                      className="font-mono text-[9px] mt-1 flex-shrink-0"
                      style={{ color: 'var(--m-muted-3)' }}
                    >
                      —
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--m-ink-4)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        lineHeight: 1.55,
                      }}
                    >
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Process */}
            <div>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-6"
                style={{ color: 'var(--m-muted)' }}
              >
                How We Work
              </p>
              <div className="space-y-0">
                {service.process.map((step, i) => (
                  <div
                    key={step.step}
                    className="flex gap-5 py-5"
                    style={{
                      borderBottom:
                        i < service.process.length - 1
                          ? '1px solid rgba(var(--m-ink-rgb),0.07)'
                          : 'none',
                    }}
                  >
                    <span
                      className="font-mono flex-shrink-0"
                      style={{
                        fontSize: 10,
                        color: 'var(--m-muted-4)',
                        letterSpacing: '0.08em',
                        marginTop: 3,
                        minWidth: 24,
                      }}
                    >
                      {step.step}
                    </span>
                    <div>
                      <p
                        className="font-semibold mb-1"
                        style={{
                          fontSize: 13,
                          color: 'var(--m-ink)',
                          fontFamily:
                            "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {step.title}
                      </p>
                      <p
                        style={{
                          fontSize: 13,
                          color: 'var(--m-ink-6)',
                          fontFamily:
                            "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                          lineHeight: 1.6,
                        }}
                      >
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQs */}
            <div>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-2"
                style={{ color: 'var(--m-muted)' }}
              >
                Frequently Asked Questions
              </p>
              <div>
                {service.faqs.map((faq) => (
                  <FAQItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Deliverables */}
            <div style={{ borderTop: '2px solid var(--m-ink)', paddingTop: 20 }}>
              <p
                className="font-mono text-[10px] uppercase tracking-widest mb-4"
                style={{ color: 'var(--m-muted)' }}
              >
                What You Get
              </p>
              <ul className="space-y-2.5">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2.5">
                    <CheckCircle2
                      className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--m-ink)' }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        color: 'var(--m-ink-4)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        lineHeight: 1.5,
                      }}
                    >
                      {d}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Timeline */}
            <div style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.1)', paddingTop: 20 }}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-3.5 h-3.5" style={{ color: 'var(--m-muted)' }} />
                <p
                  className="font-mono text-[10px] uppercase tracking-widest"
                  style={{ color: 'var(--m-muted)' }}
                >
                  Typical Timeline
                </p>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--m-ink-5)',
                  fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.6,
                }}
              >
                {service.timeline}
              </p>
            </div>

            {/* CTA */}
            <div style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.1)', paddingTop: 20 }}>
              <p
                className="font-bold uppercase mb-2"
                style={{
                  fontFamily: "'Archivo Narrow', sans-serif",
                  fontSize: 14,
                  color: 'var(--m-ink)',
                  letterSpacing: '0.04em',
                }}
              >
                Ready to get started?
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--m-ink-6)',
                  fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.6,
                  marginBottom: 16,
                }}
              >
                Book a free 30-minute consultation to discuss your specific needs.
              </p>
              <Link
                href="/book-a-consultation"
                className="inline-flex items-center gap-2 w-full justify-center font-mono uppercase text-[10px] tracking-widest py-3 transition-opacity hover:opacity-80"
                style={{ background: 'var(--m-ink)', color: 'var(--m-bg)', textDecoration: 'none' }}
              >
                [ <CtaLabel mobile={CTA_BOOKING} desktop="Book a Free Consultation" /> ]
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Related services */}
            {related.length > 0 && (
              <div style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.1)', paddingTop: 20 }}>
                <p
                  className="font-mono text-[10px] uppercase tracking-widest mb-4"
                  style={{ color: 'var(--m-muted)' }}
                >
                  Related Services
                </p>
                <div className="space-y-2">
                  {related.map((r) => {
                    const rel = serviceData[r];
                    if (!rel) return null;
                    const RelIcon = rel.icon;
                    return (
                      <Link
                        key={r}
                        href={`/services/${r}`}
                        className="flex items-center gap-3 py-2.5 group transition-opacity hover:opacity-70"
                        style={{
                          borderBottom: '1px solid rgba(var(--m-ink-rgb),0.07)',
                          textDecoration: 'none',
                        }}
                      >
                        <RelIcon
                          className="w-3.5 h-3.5 flex-shrink-0"
                          style={{ color: 'var(--m-muted)' }}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            color: 'var(--m-ink-3)',
                            fontFamily:
                              "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {rel.title}
                        </span>
                        <ArrowRight
                          className="w-3 h-3 ml-auto"
                          style={{ color: 'var(--m-muted-4)' }}
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
